import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { SearchIcon, ArrowUpIcon, ArrowDownIcon, EyeIcon } from '@heroicons/react/outline';
import BackToDashboardButton from '../components/common/BackToDashboardButton';

// Helper function to safely format currency amounts
const formatCurrency = (amount: any): string => {
  if (typeof amount === 'number') {
    return amount.toFixed(2);
  }
  if (typeof amount === 'string') {
    const parsed = parseFloat(amount);
    return isNaN(parsed) ? '0.00' : parsed.toFixed(2);
  }
  return '0.00';
};

// Types
interface OrderItem {
  id: number;
  listing_id: number;
  quantity: number;
  price_per_item: number;
  line_total?: number;
  listing_title?: string;
  listing_image_url?: string;
  listing_description?: string;
}

interface TransactionBreakdown {
  subtotal: number;
  shipping_cost: number;
  tax_amount: number;
  total: number;
  items_count: number;
  seller_payout_amount?: number;
  shipping_label_cost?: number;
  stripe_fees?: number;
  projected_income?: number;
}

interface Order {
  id: number;
  buyer_id: number;
  listing_id?: number;
  price_at_purchase?: number;
  total_amount?: number;
  status: 'pending' | 'pending_sale' | 'processing' | 'shipped' | 'delivered' | 'completed' | 'cancelled' | 'disputed';
  created_at: string;
  shipping_status?: string;
  tracking_number?: string;
  listing_title?: string;
  image_url?: string;
  seller_username?: string;
  buyer_username?: string;
  shipping_info?: any;
  order_items?: OrderItem[];
  transaction_breakdown?: TransactionBreakdown;
  subtotal?: number;
  tax_amount?: number;
  has_shipping_label?: boolean;
}

type SortField = 'id' | 'created_at' | 'price_at_purchase' | 'status';
type SortDirection = 'asc' | 'desc';

interface OrderDetailsModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateOrder: (updatedOrder: Order) => void;
}

// Order Details Modal Component
const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ order, isOpen, onClose, onUpdateOrder }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [showTrackingInput, setShowTrackingInput] = useState(false);
  const [labelSize, setLabelSize] = useState<string>('8.5x11');
  const [labelFormat, setLabelFormat] = useState<string>('PDF');
  const [showServiceVerification, setShowServiceVerification] = useState(false);
  const [shippingServicePreview, setShippingServicePreview] = useState<any>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [detailedOrder, setDetailedOrder] = useState<Order | null>(null);
  const [, setLoadingDetails] = useState(false); // Loading state for order details fetch
  const [labelInfo, setLabelInfo] = useState<{ tracking_number: string; label_url: string; generated_at: string } | null>(null);
  
  // Editable dimensions state
  const [editedWeight, setEditedWeight] = useState<string>('');
  const [editedLength, setEditedLength] = useState<string>('');
  const [editedWidth, setEditedWidth] = useState<string>('');
  const [editedHeight, setEditedHeight] = useState<string>('');
  const [recalculatingCost, setRecalculatingCost] = useState(false);
  const [editedBuyerPaidShipping, setEditedBuyerPaidShipping] = useState<string>('');
  const [updatingShippingCost, setUpdatingShippingCost] = useState(false);
  
  // Shipping address form state
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: ''
  });
  const [savingAddress, setSavingAddress] = useState(false);
  
  // Helper function to open label and trigger print dialog with correct orientation
  const openLabelAndPrint = (labelUrl: string) => {
    const printWindow = window.open(labelUrl, '_blank');
    
    if (printWindow) {
      setTimeout(() => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch (error) {
          console.log('Auto-print not available, label is open for manual printing');
        }
      }, 1500);
    }
  };
  
  // Fetch detailed order information when modal opens
  useEffect(() => {
    if (isOpen && order) {
      const fetchOrderDetails = async () => {
        try {
          setLoadingDetails(true);
          const token = localStorage.getItem('token');
          if (!token) return;
          
          const response = await axios.get(`/api/orders/${order.id}/details`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.data) {
            setDetailedOrder(response.data);
            // Load shipping address if available
            if (response.data.shipping_address) {
              setShippingAddress({
                name: response.data.shipping_address.name || '',
                address_line1: response.data.shipping_address.address_line1 || '',
                address_line2: response.data.shipping_address.address_line2 || '',
                city: response.data.shipping_address.city || '',
                state: response.data.shipping_address.state || '',
                postal_code: response.data.shipping_address.postal_code || '',
                country: response.data.shipping_address.country || 'US',
                phone: response.data.shipping_address.phone || ''
              });
            }
          }
        } catch (error) {
          console.error('Error fetching order details:', error);
          // Fallback to using the order prop if API fails
          setDetailedOrder(order);
        } finally {
          setLoadingDetails(false);
        }
      };
      
      // Fetch label information if order has a shipping label
      const fetchLabelInfo = async () => {
        if (order.has_shipping_label || order.tracking_number) {
          try {
            const token = localStorage.getItem('token');
            if (!token) return;
            
            const response = await axios.get(`/api/shipping/labels/order/${order.id}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.data) {
              setLabelInfo({
                tracking_number: response.data.tracking_number,
                label_url: response.data.label_url,
                generated_at: response.data.generated_at
              });
            }
          } catch (error: any) {
            // If label doesn't exist or error, just log it (not critical)
            if (error.response?.status !== 404) {
              console.error('Error fetching label info:', error);
            }
            setLabelInfo(null);
          }
        } else {
          setLabelInfo(null);
        }
      };
      
      fetchOrderDetails();
      fetchLabelInfo();
    } else {
      // Reset when modal closes
      setLabelInfo(null);
    }
  }, [isOpen, order]);

  // Initialize PWE (#10 envelope) defaults when modal opens
  useEffect(() => {
    if (isOpen && order) {
      // Set PWE #10 envelope defaults if dimensions are empty
      // #10 envelope: 9.5" x 4.125" x 0.25", weight ~0.1 lbs (1.6 oz)
      setEditedWeight(prev => prev || '0.1');
      setEditedLength(prev => prev || '9.5');
      setEditedWidth(prev => prev || '4.125');
      setEditedHeight(prev => prev || '0.25');
    } else {
      // Reset dimensions when modal closes
      setEditedWeight('');
      setEditedLength('');
      setEditedWidth('');
      setEditedHeight('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, order]);
  
  // Use detailed order if available, otherwise fall back to order prop
  const displayOrder = detailedOrder || order;
  
  if (!isOpen || !order || !displayOrder) return null;
  


  // Function to mark order as shipped
  const handleMarkAsShipped = async () => {
    try {
      setIsProcessing(true);
      setStatusMessage(null);
      
      // Show warning for pending orders
      if (order.status === 'pending' || order.status === 'pending_sale') {
        setStatusMessage({
          type: 'error',
          text: 'Note: The system may only allow paid orders to be marked as shipped. If you get an error, please check the order payment status.'
        });
        await new Promise(resolve => setTimeout(resolve, 1500)); // Show message for 1.5 seconds
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMessage({
          type: 'error',
          text: 'Authentication required. Please log in again.'
        });
        return;
      }

      await axios.put(`/api/orders/${order.id}/status`, {
        status: 'shipped',
        trackingNumber: trackingNumber || undefined
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Create updated order object with correct typing
      const updatedOrder: Order = {
        ...order,
        status: 'shipped',
        tracking_number: trackingNumber || order.tracking_number
      };
      
      // Update the order in the parent component's state
      onUpdateOrder(updatedOrder);
      
      setStatusMessage({
        type: 'success',
        text: `Order marked as shipped successfully!${trackingNumber ? ` Tracking number: ${trackingNumber}` : ''}`
      });
      
      // Reset tracking input
      setTrackingNumber('');
      setShowTrackingInput(false);
    } catch (error: any) {
      console.error('Error marking order as shipped:', error);
      
      // Check if the error has a response with data
      if (error.response && error.response.data) {
        // If we have a specific error message from the server, display it
        setStatusMessage({
          type: 'error',
          text: error.response.data.message || 'Failed to mark order as shipped. Please try again.'
        });
      } else {
        setStatusMessage({
          type: 'error',
          text: 'Failed to mark order as shipped. Please try again.'
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Function to preview shipping service before generating label
  const handlePreviewShippingService = async () => {
    try {
      setLoadingPreview(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMessage({
          type: 'error',
          text: 'Authentication required. Please log in again.'
        });
        return;
      }
      
      const response = await axios.get(`/api/shipping/labels/preview/${order.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const preview = response.data.shippingService;
        setShippingServicePreview(preview);
        
        // Initialize editable dimensions from the response, or use PWE #10 envelope defaults
        if (preview.packageInfo && preview.packageInfo.dimensions) {
          setEditedWeight(preview.packageInfo.weight ? preview.packageInfo.weight.toFixed(2) : '0.1');
          setEditedLength(preview.packageInfo.dimensions.length ? preview.packageInfo.dimensions.length.toString() : '9.5');
          setEditedWidth(preview.packageInfo.dimensions.width ? preview.packageInfo.dimensions.width.toString() : '4.125');
          setEditedHeight(preview.packageInfo.dimensions.height ? preview.packageInfo.dimensions.height.toString() : '0.25');
        } else {
          // Use PWE #10 envelope defaults if no package info
          setEditedWeight('0.1');
          setEditedLength('9.5');
          setEditedWidth('4.125');
          setEditedHeight('0.25');
        }
        
        // Initialize editable buyer paid shipping if cost mismatch detected
        if (preview.costComparison && !preview.costComparison.matches) {
          setEditedBuyerPaidShipping(preview.costComparison.buyerPaid.toFixed(2));
        } else {
          setEditedBuyerPaidShipping('');
        }
        
        setShowServiceVerification(true);
      }
    } catch (error: any) {
      console.error('Error previewing shipping service:', error);
      setStatusMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to preview shipping service'
      });
    } finally {
      setLoadingPreview(false);
    }
  };
  
  // Function to recalculate cost when dimensions are edited
  const handleRecalculateCost = async () => {
    try {
      setRecalculatingCost(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Build query params with edited dimensions
      const params = new URLSearchParams();
      if (editedWeight) params.append('weight', editedWeight);
      if (editedLength) params.append('length', editedLength);
      if (editedWidth) params.append('width', editedWidth);
      if (editedHeight) params.append('height', editedHeight);
      
      const response = await axios.get(`/api/shipping/labels/preview/${order.id}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setShippingServicePreview(response.data.shippingService);
      }
    } catch (error: any) {
      console.error('Error recalculating cost:', error);
      setStatusMessage({
        type: 'error',
        text: 'Failed to recalculate shipping cost'
      });
    } finally {
      setRecalculatingCost(false);
    }
  };

  // Function to generate shipping label (after verification)
  const handleGenerateShippingLabel = async () => {
    try {
      setIsProcessing(true);
      setStatusMessage(null);
      setShowServiceVerification(false); // Close verification modal
      
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMessage({
          type: 'error',
          text: 'Authentication required. Please log in again.'
        });
        return;
      }
      
      // Build request body with edited dimensions if changed
      const requestBody: any = {
        labelFormat: labelFormat,
        labelSize: labelSize
      };
      
      // Include edited dimensions if they differ from original
      if (shippingServicePreview?.packageInfo?.dimensionsEdited || 
          (editedWeight && parseFloat(editedWeight) !== shippingServicePreview?.packageInfo?.originalWeight) ||
          (editedLength && parseFloat(editedLength) !== shippingServicePreview?.packageInfo?.originalDimensions?.length) ||
          (editedWidth && parseFloat(editedWidth) !== shippingServicePreview?.packageInfo?.originalDimensions?.width) ||
          (editedHeight && parseFloat(editedHeight) !== shippingServicePreview?.packageInfo?.originalDimensions?.height)) {
        requestBody.weight = parseFloat(editedWeight);
        requestBody.length = parseFloat(editedLength);
        requestBody.width = parseFloat(editedWidth);
        requestBody.height = parseFloat(editedHeight);
      }
      
      const response = await axios.post(`/api/shipping/labels/generate/${order.id}`, requestBody, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Handle both test labels and real labels
      if (response.data.success) {
        // Update order with tracking number AND status
        // Backend automatically sets status to 'shipped' when label is generated
        if (response.data.tracking_number) {
          const updatedOrder: Order = {
            ...order,
            tracking_number: response.data.tracking_number,
            status: response.data.order_status || 'shipped' // Update status to shipped
          };
          onUpdateOrder(updatedOrder);
        }
        
        // Check if we have a label URL (real label) or just tracking number (test label)
        if (response.data.label_url) {
          // For ZPL/EPL2 formats, download the file instead of printing
          if (labelFormat === 'ZPL' || labelFormat === 'EPL2') {
            const extension = labelFormat === 'ZPL' ? 'zpl' : 'epl2';
            const filename = `shipping-label-${order.id}-${response.data.tracking_number || 'label'}.${extension}`;
            
            const link = document.createElement('a');
            link.href = response.data.label_url;
            link.download = filename;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            setStatusMessage({
              type: 'success',
              text: `Shipping label generated successfully! Order status updated to "Shipped". Tracking number: ${response.data.tracking_number}. Label file downloaded - open with your label printer software to print.`
            });
          } else {
            // For PDF/PNG, open in new tab and trigger print dialog
            openLabelAndPrint(response.data.label_url);
            setStatusMessage({
              type: 'success',
              text: `Shipping label generated successfully! Order status updated to "Shipped". Tracking number: ${response.data.tracking_number}. Print dialog will open automatically.`
            });
          }
        } else {
          // Test label - show success message with note about contacting USPS
          setStatusMessage({
            type: 'success',
            text: `Test label generated with tracking number: ${response.data.tracking_number}. This is a test label - contact USPS to add the 'payments' scope to your account for real labels.`
          });
        }
      }
    } catch (error: any) {
      console.error('Error generating shipping label:', error);
      
      // Check if this is the "label already exists" error
      if (error.response?.status === 400 && 
          error.response?.data?.error?.includes('Shipping label already generated')) {
        
        // Get the existing tracking number from the error response
        const existingTrackingNumber = error.response.data.tracking_number;
        
        if (existingTrackingNumber) {
          // Update the order with the existing tracking number
          const updatedOrder: Order = {
            ...order,
            tracking_number: existingTrackingNumber
          };
          onUpdateOrder(updatedOrder);
        }
        
        setStatusMessage({
          type: 'info',
          text: `A shipping label already exists for this order. Tracking number: ${existingTrackingNumber || 'Available in order details'}`
        });
      } else if (error.response?.status === 402) {
        // Handle EasyPost payment/funding issues
        const errorMessage = error.response?.data?.error || 
                            error.response?.data?.message || 
                            'Insufficient funds in EasyPost account. Please add funds to your EasyPost account to generate shipping labels.';
        setStatusMessage({
          type: 'error',
          text: errorMessage
        });
      } else {
        // Handle other errors
        const errorMessage = error.response?.data?.error || 
                            error.response?.data?.message || 
                            error.message || 
                            'Failed to generate shipping label';
        setStatusMessage({
          type: 'error',
          text: errorMessage
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Function to print/view existing shipping label
  const handlePrintLabel = async () => {
    try {
      setIsProcessing(true);
      setStatusMessage(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMessage({
          type: 'error',
          text: 'Authentication required. Please log in again.'
        });
        return;
      }
      
      // Fetch the existing label
      const response = await axios.get(`/api/shipping/labels/order/${order.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.label_url) {
        // Open label in new tab and trigger print dialog
        openLabelAndPrint(response.data.label_url);
        setStatusMessage({
          type: 'success',
          text: 'Label opened. Print dialog will open automatically.'
        });
      } else {
        setStatusMessage({
          type: 'error',
          text: 'Label URL not available. Please contact support.'
        });
      }
    } catch (error: any) {
      console.error('Error retrieving shipping label:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to retrieve shipping label';
      setStatusMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Function to download shipping label directly (for dedicated label printers)
  const handleDownloadLabel = async () => {
    try {
      setIsProcessing(true);
      setStatusMessage(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMessage({
          type: 'error',
          text: 'Authentication required. Please log in again.'
        });
        return;
      }
      
      // Fetch the existing label
      const response = await axios.get(`/api/shipping/labels/order/${order.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.label_url) {
        // For dedicated label printers, download the file directly
        // This bypasses the print dialog and scaling wrapper
        // Determine file extension based on format
        const format = labelFormat || 'PDF';
        const extension = format === 'ZPL' ? 'zpl' : format === 'EPL2' ? 'epl2' : format === 'PNG' ? 'png' : 'pdf';
        const filename = `shipping-label-${order.id}-${order.tracking_number || 'label'}.${extension}`;
        
        const link = document.createElement('a');
        link.href = response.data.label_url;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setStatusMessage({
          type: 'success',
          text: format === 'ZPL' || format === 'EPL2' 
            ? 'Label file downloaded. Open with your label printer software (Zebra Setup Utilities, Dymo Label Software, etc.) to print.'
            : 'Label downloaded. Use your label printer software to print the file.'
        });
      } else {
        setStatusMessage({
          type: 'error',
          text: 'Label URL not available. Please contact support.'
        });
      }
    } catch (error: any) {
      console.error('Error downloading shipping label:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to download shipping label';
      setStatusMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Function to mark order as completed
  const handleMarkAsCompleted = async () => {
    try {
      setIsProcessing(true);
      setStatusMessage(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMessage({
          type: 'error',
          text: 'Authentication required. Please log in again.'
        });
        return;
      }
      
      await axios.put(`/api/orders/${order.id}/status`, {
        status: 'completed'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Create updated order object with correct typing
      const updatedOrder: Order = {
        ...order,
        status: 'completed'
      };
      
      // Update the order in the parent component's state
      onUpdateOrder(updatedOrder);
      
      setStatusMessage({
        type: 'success',
        text: 'Order marked as completed successfully!'
      });
    } catch (error: any) {
      console.error('Error marking order as completed:', error);
      
      // Check if the error has a response with data
      if (error.response && error.response.data) {
        // If we have a specific error message from the server, display it
        setStatusMessage({
          type: 'error',
          text: error.response.data.message || 'Failed to mark order as completed. Please try again.'
        });
      } else {
        setStatusMessage({
          type: 'error',
          text: 'Failed to mark order as completed. Please try again.'
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Function to cancel order
  const handleCancelOrder = async () => {
    try {
      setIsProcessing(true);
      setStatusMessage(null);
      
      // Confirm cancellation
      if (!window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) {
        return;
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMessage({
          type: 'error',
          text: 'Authentication required. Please log in again.'
        });
        return;
      }
      
      await axios.put(`/api/orders/${order.id}/status`, {
        status: 'cancelled'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Create updated order object with correct typing
      const updatedOrder: Order = {
        ...order,
        status: 'cancelled'
      };
      
      // Update the order in the parent component's state
      onUpdateOrder(updatedOrder);
      
      setStatusMessage({
        type: 'success',
        text: 'Order cancelled successfully!'
      });
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      
      // Check if the error has a response with data
      if (error.response && error.response.data) {
        // If we have a specific error message from the server, display it
        setStatusMessage({
          type: 'error',
          text: error.response.data.message || 'Failed to cancel order. Please try again.'
        });
      } else {
        setStatusMessage({
          type: 'error',
          text: 'Failed to cancel order. Please try again.'
        });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-6xl max-h-[95vh] overflow-y-auto mx-auto">
        <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pt-2 pb-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Order #{order.id}</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>
        
        {statusMessage && (
          <div className={`mb-4 p-3 rounded ${
            statusMessage.type === 'success' 
              ? 'bg-green-100 text-green-800' 
              : statusMessage.type === 'error'
              ? 'bg-red-100 text-red-800'
              : 'bg-blue-100 text-blue-800'
          }`}>
            {statusMessage.text}
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-lg font-medium mb-2">Order Details</h3>
            <div className="bg-gray-50 p-4 rounded">
              <p className="mb-2"><span className="font-medium">Date:</span> {new Date(order.created_at).toLocaleString()}</p>
              <p className="mb-2"><span className="font-medium">Status:</span> {order.status}</p>
              <p className="mb-2"><span className="font-medium">Amount:</span> ${formatCurrency(order.total_amount || order.price_at_purchase)}</p>
              {(labelInfo?.tracking_number || order.tracking_number) && (
                <div className="mb-2 p-2 bg-blue-50 rounded border border-blue-200">
                  <p className="mb-1"><span className="font-medium">Tracking Number:</span></p>
                  <p className="text-lg font-semibold text-blue-900">{labelInfo?.tracking_number || order.tracking_number}</p>
                  {labelInfo?.generated_at && (
                    <p className="text-xs text-gray-600 mt-1">Label generated: {new Date(labelInfo.generated_at).toLocaleString()}</p>
                  )}
                </div>
              )}
              {order.shipping_status && (
                <p className="mb-2"><span className="font-medium">Shipping Status:</span> {order.shipping_status}</p>
              )}
              {/* Display shipping service if available */}
              {displayOrder.shipping_info && (() => {
                try {
                  const shippingInfo = typeof displayOrder.shipping_info === 'string' 
                    ? JSON.parse(order.shipping_info) 
                    : order.shipping_info;
                  if (shippingInfo.service || shippingInfo.carrier) {
                    return (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="mb-1"><span className="font-medium">Shipping Service:</span></p>
                        <p className="text-sm text-gray-700">
                          {shippingInfo.carrier && <span className="font-medium">{shippingInfo.carrier}</span>}
                          {shippingInfo.service && (
                            <span className="ml-1">
                              {shippingInfo.service === 'First Class Mail (PWE)' ? '📮 PWE' : shippingInfo.service}
                            </span>
                          )}
                        </p>
                        {shippingInfo.shipping_cost && (
                          <p className="text-sm text-gray-600 mt-1">
                            Cost: ${parseFloat(shippingInfo.shipping_cost).toFixed(2)}
                          </p>
                        )}
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
                return null;
              })()}
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-2">Buyer Information</h3>
            <div className="bg-gray-50 p-4 rounded">
              <p className="mb-2"><span className="font-medium">Username:</span> {displayOrder.buyer_username || 'Unknown'}</p>
              <p className="mb-2"><span className="font-medium">Buyer ID:</span> {displayOrder.buyer_id}</p>
            </div>
          </div>
        </div>
        
        {/* Order Items and Transaction Breakdown */}
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Order Items</h3>
          <div className="bg-gray-50 p-4 rounded space-y-3">
            {/* Display order items if available from order details API */}
            {displayOrder.order_items && displayOrder.order_items.length > 0 ? (
              <>
                {displayOrder.order_items.map((item: any, index: number) => (
                  <div key={item.id || index} className="flex items-center border-b border-gray-200 pb-3 last:border-0 last:pb-0">
                    {item.listing_image_url && (
                      <img 
                        src={item.listing_image_url} 
                        alt={item.listing_title || 'Item'} 
                        className="w-16 h-16 object-cover mr-4 rounded"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/images/placeholder.jpg';
                        }}
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.listing_title || 'Item'}</p>
                      <p className="text-sm text-gray-600">Qty: {item.quantity} × ${formatCurrency(item.price_per_item)} = ${formatCurrency(item.line_total || (item.price_per_item * item.quantity))}</p>
                    </div>
                  </div>
                ))}
                
                {/* Transaction Breakdown */}
                {displayOrder.transaction_breakdown && (
                  <div className="mt-4 pt-4 border-t-2 border-gray-300 space-y-2">
                    <h4 className="font-semibold text-gray-900 mb-2">Transaction Breakdown</h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Subtotal ({displayOrder.transaction_breakdown.items_count} item{displayOrder.transaction_breakdown.items_count !== 1 ? 's' : ''}):</span>
                      <span className="font-medium text-gray-900">${formatCurrency(displayOrder.transaction_breakdown.subtotal)}</span>
                    </div>
                    {displayOrder.transaction_breakdown.shipping_cost > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Shipping:</span>
                        <span className="font-medium text-gray-900">${formatCurrency(displayOrder.transaction_breakdown.shipping_cost)}</span>
                      </div>
                    )}
                    {displayOrder.transaction_breakdown.tax_amount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Tax:</span>
                        <span className="font-medium text-gray-900">${formatCurrency(displayOrder.transaction_breakdown.tax_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-300">
                      <span className="text-gray-900">Total Paid:</span>
                      <span className="text-gray-900">${formatCurrency(displayOrder.transaction_breakdown.total)}</span>
                    </div>
                    
                    {/* Seller Income Breakdown - Only show for sellers */}
                    {displayOrder.transaction_breakdown.seller_payout_amount !== undefined && (
                      <div className="mt-4 pt-4 border-t-2 border-blue-300 bg-blue-50 rounded-lg p-4 space-y-2">
                        <h4 className="font-semibold text-blue-900 mb-2">Your Income Breakdown</h4>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-700">Item Subtotal:</span>
                          <span className="font-medium text-gray-900">${formatCurrency(displayOrder.transaction_breakdown.subtotal)}</span>
                        </div>
                        {displayOrder.transaction_breakdown.shipping_cost > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700">Shipping Collected:</span>
                            <span className="font-medium text-gray-900">${formatCurrency(displayOrder.transaction_breakdown.shipping_cost)}</span>
                          </div>
                        )}
                        {(displayOrder.transaction_breakdown.shipping_label_cost ?? 0) > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700">Shipping Label Cost:</span>
                            <span className="font-medium text-red-600">-${formatCurrency(displayOrder.transaction_breakdown.shipping_label_cost ?? 0)}</span>
                          </div>
                        )}
                        {(displayOrder.transaction_breakdown.stripe_fees ?? 0) > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700">Stripe Processing Fees:</span>
                            <span className="font-medium text-red-600">-${formatCurrency(displayOrder.transaction_breakdown.stripe_fees ?? 0)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg font-bold pt-2 border-t-2 border-blue-400">
                          <span className="text-blue-900">Projected Income:</span>
                          <span className="text-blue-900">${formatCurrency(displayOrder.transaction_breakdown.seller_payout_amount || displayOrder.transaction_breakdown.projected_income || 0)}</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-2 italic">
                          * Payout processed after delivery confirmation
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              // Fallback to single item display if order_items not available
              <div className="flex items-center">
                {displayOrder.image_url && (
                  <img 
                    src={displayOrder.image_url} 
                    alt={displayOrder.listing_title || 'Item'} 
                    className="w-20 h-20 object-cover mr-4 rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/placeholder.jpg';
                    }}
                  />
                )}
                <div>
                  <p className="font-medium">{displayOrder.listing_title || 'Item'}</p>
                  {displayOrder.listing_id && (
                    <p className="text-sm text-gray-600">Listing ID: {displayOrder.listing_id}</p>
                  )}
                  <p className="text-sm text-gray-600">Price: ${formatCurrency(displayOrder.total_amount || displayOrder.price_at_purchase)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Reprint Label Button - Show if label exists */}
        {labelInfo && labelInfo.label_url && (
          <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-900 mb-1">Shipping Label Available</p>
                <p className="text-xs text-green-700">You can reprint the label at any time</p>
              </div>
              <button
                onClick={() => {
                  if (labelInfo.label_url) {
                    openLabelAndPrint(labelInfo.label_url);
                  }
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Reprint Label
              </button>
            </div>
          </div>
        )}
        
        <div className="flex justify-end space-x-3 border-t pt-4">
          {/* Cancel Order Button - Available for pending, pending_sale, and processing orders */}
          {(order.status === 'pending' || order.status === 'pending_sale' || order.status === 'processing') && (
            <button
              onClick={handleCancelOrder}
              disabled={isProcessing}
              className={`bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md flex items-center ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isProcessing ? 'Processing...' : 'Cancel Order'}
            </button>
          )}
          
          {/* Tracking Number Input */}
          {(order.status === 'pending' || order.status === 'pending_sale' || order.status === 'processing') && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Tracking Number (Optional)</label>
                <button
                  onClick={() => setShowTrackingInput(!showTrackingInput)}
                  className="text-blue-600 hover:text-blue-800 text-sm underline"
                >
                  {showTrackingInput ? 'Hide' : 'Add Tracking Number'}
                </button>
              </div>
              {showTrackingInput && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Enter tracking number (e.g., 1Z999AA1234567890)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          )}

          {/* Shipping Actions */}
          {(order.status === 'pending' || order.status === 'pending_sale' || order.status === 'processing') && (
            <>
              <button
                onClick={handleMarkAsShipped}
                disabled={isProcessing}
                className={`bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isProcessing ? 'Processing...' : 'Mark as Shipped'}
              </button>
              
              {/* Label Size Selection */}
              <div className="flex items-center gap-2">
                <label htmlFor="labelSize" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Label Size:
                </label>
                <select
                  id="labelSize"
                  value={labelSize}
                  onChange={(e) => setLabelSize(e.target.value)}
                  disabled={isProcessing}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="8.5x11">Standard Paper — Print &amp; Cut</option>
                  <option value="5.5x8.5">Avery 8126 Half-Sheet Labels</option>
                  <option value="4x6">4x6 Thermal Label Printer</option>
                </select>
                {labelSize === '5.5x8.5' && (
                  <span className="text-xs text-gray-500 ml-2">
                    Set paper to Half Letter in print dialog
                  </span>
                )}
                
                <label htmlFor="labelFormat" className="text-sm font-medium text-gray-700 whitespace-nowrap ml-2">
                  Format:
                </label>
                <select
                  id="labelFormat"
                  value={labelFormat}
                  onChange={(e) => setLabelFormat(e.target.value)}
                  title={labelFormat === 'ZPL' || labelFormat === 'EPL2' ? 'For dedicated label printers (Zebra, Dymo, etc.). Use Download button to save file.' : 'PDF for standard printers, ZPL/EPL2 for dedicated label printers'}
                  disabled={isProcessing}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="PDF">PDF (Standard Printers)</option>
                  <option value="PNG">PNG (Image)</option>
                  <option value="ZPL">ZPL (Zebra/Thermal Printers)</option>
                  <option value="EPL2">EPL2 (Dymo/Other Thermal)</option>
                </select>
                {(labelFormat === 'ZPL' || labelFormat === 'EPL2') && (
                  <span className="text-xs text-blue-600 ml-2" title="Use the Download button to save the file for your label printer software">
                    📥 Use Download button
                  </span>
                )}
              </div>
              
              <button
                onClick={handlePreviewShippingService}
                disabled={isProcessing || loadingPreview}
                className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center ${isProcessing || loadingPreview ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {loadingPreview ? 'Loading...' : 'Preview & Generate Label'}
              </button>
            </>
          )}
          
          {/* Completion Actions */}
          {(order.status === 'shipped' || order.status === 'delivered') && (
            <>
              <button
                onClick={handleMarkAsCompleted}
                disabled={isProcessing}
                className={`bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isProcessing ? 'Processing...' : 'Mark as Completed'}
              </button>
              {order.tracking_number && (
                <div className="flex gap-2">
                  <button
                    onClick={handlePrintLabel}
                    disabled={isProcessing}
                    className={`bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-md flex items-center ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
                    title="Print label with automatic scaling (for standard printers)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    {isProcessing ? 'Loading...' : 'Print Label'}
                  </button>
                  <button
                    onClick={handleDownloadLabel}
                    disabled={isProcessing}
                    className={`bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md flex items-center ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
                    title="Download label file directly (for dedicated label printers like Zebra, Dymo, etc.)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Shipping Service Verification Modal */}
      {showServiceVerification && shippingServicePreview && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[95vh] overflow-y-auto mx-auto">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Verify Shipping Details</h3>
            
            <div className="mb-4 space-y-4">
              {/* Shipping Service Info */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-900 mb-2">Selected Shipping Service:</p>
                <div className="space-y-1">
                  <p className="text-lg font-semibold text-gray-900">
                    {shippingServicePreview.carrier} - {shippingServicePreview.serviceDisplayName}
                  </p>
                  {shippingServicePreview.isPWE && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      📮 PWE (Plain White Envelope)
                    </span>
                  )}
                </div>
              </div>
              
              {/* Cost Comparison */}
              {shippingServicePreview.costComparison && (
                <div className={`p-4 rounded-lg border ${
                  shippingServicePreview.costComparison.matches 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <p className="text-sm font-medium mb-3">
                    {shippingServicePreview.costComparison.matches ? '✅' : '⚠️'} Cost Comparison:
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Buyer Paid:</p>
                      {!shippingServicePreview.costComparison.matches ? (
                        <div>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editedBuyerPaidShipping || shippingServicePreview.costComparison.buyerPaid.toFixed(2)}
                            onChange={(e) => setEditedBuyerPaidShipping(e.target.value)}
                            className="w-full px-2 py-1 border border-yellow-300 rounded text-lg font-semibold text-gray-900 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">Edit to match label cost</p>
                        </div>
                      ) : (
                        <p className="text-lg font-semibold text-gray-900">
                          ${shippingServicePreview.costComparison.buyerPaid.toFixed(2)}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-gray-600">
                        Actual Label Cost:
                        {recalculatingCost && <span className="ml-2 text-xs text-blue-600">(Recalculating...)</span>}
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        ${shippingServicePreview.costComparison.actualLabelCost.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  {!shippingServicePreview.costComparison.matches && (
                    <div className="mt-3 p-2 bg-yellow-100 rounded text-sm">
                      <p className="font-medium text-yellow-800">
                        {shippingServicePreview.costComparison.warning}
                      </p>
                      <p className="text-yellow-700">
                        Difference: ${Math.abs(shippingServicePreview.costComparison.difference).toFixed(2)} 
                        {shippingServicePreview.costComparison.difference > 0 ? ' (over)' : ' (under)'}
                      </p>
                      {editedBuyerPaidShipping && parseFloat(editedBuyerPaidShipping) !== shippingServicePreview.costComparison.buyerPaid && (
                        <button
                          onClick={async () => {
                            try {
                              setUpdatingShippingCost(true);
                              const token = localStorage.getItem('token');
                              if (!token) return;
                              
                              const response = await axios.put(
                                `/api/orders/${order.id}/shipping-cost`,
                                { shipping_cost: parseFloat(editedBuyerPaidShipping) },
                                {
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  }
                                }
                              );
                              
                              if (response.data.success) {
                                // Refresh the preview to show updated comparison
                                await handlePreviewShippingService();
                                setStatusMessage({
                                  type: 'success',
                                  text: 'Shipping cost updated successfully. Payout amount recalculated.'
                                });
                                // Refresh order details
                                const detailsResponse = await axios.get(`/api/orders/${order.id}/details`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (detailsResponse.data) {
                                  const updatedOrder = { ...order, ...detailsResponse.data };
                                  onUpdateOrder(updatedOrder);
                                }
                              }
                            } catch (error: any) {
                              console.error('Error updating shipping cost:', error);
                              setStatusMessage({
                                type: 'error',
                                text: error.response?.data?.message || 'Failed to update shipping cost'
                              });
                            } finally {
                              setUpdatingShippingCost(false);
                            }
                          }}
                          disabled={updatingShippingCost}
                          className="mt-2 px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 disabled:opacity-50"
                        >
                          {updatingShippingCost ? 'Updating...' : 'Update Shipping Cost'}
                        </button>
                      )}
                    </div>
                  )}
                  {shippingServicePreview.costComparison.calculationError && (
                    <p className="mt-2 text-xs text-gray-500 italic">
                      Note: {shippingServicePreview.costComparison.calculationError}
                    </p>
                  )}
                </div>
              )}
              
              {/* Shipping Address Form */}
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex justify-between items-center mb-3">
                  <p className="font-medium text-gray-700">Shipping Address:</p>
                  {shippingServicePreview.costComparison?.calculationError && (
                    <span className="text-xs text-red-600 font-medium">⚠️ Required for cost calculation</span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-600 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={shippingAddress.name}
                      onChange={(e) => setShippingAddress({...shippingAddress, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Recipient name"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-600 mb-1">Address Line 1 *</label>
                    <input
                      type="text"
                      value={shippingAddress.address_line1}
                      onChange={(e) => setShippingAddress({...shippingAddress, address_line1: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Street address"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-600 mb-1">Address Line 2</label>
                    <input
                      type="text"
                      value={shippingAddress.address_line2}
                      onChange={(e) => setShippingAddress({...shippingAddress, address_line2: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Apartment, suite, etc. (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">City *</label>
                    <input
                      type="text"
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">State *</label>
                    <input
                      type="text"
                      value={shippingAddress.state}
                      onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="State"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">ZIP Code *</label>
                    <input
                      type="text"
                      value={shippingAddress.postal_code}
                      onChange={(e => setShippingAddress({...shippingAddress, postal_code: e.target.value}))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="ZIP code"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={shippingAddress.phone}
                      onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Phone number (optional)"
                    />
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setSavingAddress(true);
                      const token = localStorage.getItem('token');
                      if (!token) return;
                      
                      // Validate required fields
                      if (!shippingAddress.name || !shippingAddress.address_line1 || !shippingAddress.city || !shippingAddress.state || !shippingAddress.postal_code) {
                        setStatusMessage({
                          type: 'error',
                          text: 'Please fill in all required address fields'
                        });
                        return;
                      }
                      
                      // Save shipping address to order
                      const response = await axios.put(
                        `/api/orders/${order.id}/shipping-address`,
                        shippingAddress,
                        {
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          }
                        }
                      );
                      
                      if (response.data.message || response.data.shipping_address_id) {
                        setStatusMessage({
                          type: 'success',
                          text: 'Shipping address saved successfully. Recalculating shipping costs...'
                        });
                        // Refresh order details and recalculate
                        const detailsResponse = await axios.get(`/api/orders/${order.id}/details`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (detailsResponse.data) {
                          setDetailedOrder(detailsResponse.data);
                          // Trigger cost recalculation
                          if (handleRecalculateCost) {
                            await handleRecalculateCost();
                          }
                        }
                      }
                    } catch (error: any) {
                      console.error('Error saving shipping address:', error);
                      setStatusMessage({
                        type: 'error',
                        text: error.response?.data?.message || 'Failed to save shipping address'
                      });
                    } finally {
                      setSavingAddress(false);
                    }
                  }}
                  disabled={savingAddress}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingAddress ? 'Saving...' : 'Save Shipping Address'}
                </button>
              </div>
              
              {/* Editable Package Dimensions */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-3">
                  <p className="font-medium text-gray-700">Package Dimensions:</p>
                  <button
                    onClick={handleRecalculateCost}
                    disabled={recalculatingCost}
                    className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                  >
                    {recalculatingCost ? 'Recalculating...' : 'Recalculate Cost'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Weight (lbs)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedWeight}
                      onChange={(e) => setEditedWeight(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Length (inches)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={editedLength}
                      onChange={(e) => setEditedLength(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Width (inches)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={editedWidth}
                      onChange={(e) => setEditedWidth(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Height (inches)</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={editedHeight}
                      onChange={(e) => setEditedHeight(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                {shippingServicePreview.packageInfo?.dimensionsEdited && (
                  <p className="mt-2 text-xs text-blue-600">
                    📝 Dimensions have been modified from original values
                  </p>
                )}
              </div>
              
              {/* Label Options */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="font-medium text-gray-700 mb-3">Label Options:</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Format</label>
                    <select
                      value={labelFormat}
                      onChange={(e) => setLabelFormat(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="PDF">PDF</option>
                      <option value="PNG">PNG</option>
                      <option value="ZPL">ZPL (Thermal)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Size & Orientation</label>
                    <select
                      value={labelSize}
                      onChange={(e) => setLabelSize(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="4x6">4" × 6" (Portrait)</option>
                      <option value="6x4">6" × 4" (Landscape)</option>
                      <option value="8.5x11">8.5" × 11" (Full Page)</option>
                    </select>
                    <p className="text-xs text-gray-400 mt-1">
                      {labelSize === '6x4' ? '⚠️ Select Landscape in print dialog' : labelSize === '4x6' ? 'Select Portrait in print dialog' : 'Select orientation in print dialog'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={() => {
                  setShowServiceVerification(false);
                  setShippingServicePreview(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateShippingLabel}
                disabled={isProcessing || recalculatingCost}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {isProcessing ? 'Generating...' : 'Confirm & Generate Label'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SellerOrdersPage: React.FC = () => {
  // eslint-disable-next-line no-empty-pattern
  const { /* user */ } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch seller orders
  useEffect(() => {
    fetchOrders();
  }, []);
  
  // Function to fetch orders
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await axios.get('/api/seller/orders', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && Array.isArray(response.data)) {
        // Transform the data to ensure numeric values are properly formatted
        const processedOrders = response.data.map(order => ({
          ...order,
          // Ensure price_at_purchase is a number
          price_at_purchase: typeof order.price_at_purchase === 'string' 
            ? parseFloat(order.price_at_purchase) 
            : Number(order.price_at_purchase)
        }));
        setOrders(processedOrders);
        
        // Mark unviewed orders as viewed
        const unviewedOrders = processedOrders.filter(order => !order.seller_viewed_at);
        for (const order of unviewedOrders) {
          try {
            await axios.put(`/api/seller/orders/${order.id}/viewed`, {}, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
          } catch (error) {
            console.error(`Failed to mark order ${order.id} as viewed:`, error);
          }
        }
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Unexpected data format received from server');
      }
    } catch (err) {
      console.error('Error fetching seller orders:', err);
      setError('Failed to load your orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to update a specific order in the orders array
  const updateOrder = (updatedOrder: Order) => {
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.id === updatedOrder.id ? { ...order, ...updatedOrder } : order
      )
    );
    
    // If we're currently viewing this order, update it in the modal too
    if (viewingOrder && viewingOrder.id === updatedOrder.id) {
      setViewingOrder({ ...viewingOrder, ...updatedOrder });
    }
  };

  // Filter and sort orders
  const filteredAndSortedOrders = [...orders]
    .filter(order => {
      // Handle status filtering with new status values
      let statusMatches = selectedStatus === 'All';
      if (selectedStatus === 'Pending') {
        statusMatches = order.status === 'pending' || order.status === 'pending_sale';
      } else if (selectedStatus === 'Pending Sale') {
        statusMatches = order.status === 'pending_sale';
      } else if (selectedStatus === 'Processing') {
        statusMatches = order.status === 'processing';
      } else if (selectedStatus === 'Shipped') {
        statusMatches = order.status === 'shipped';
      } else if (selectedStatus === 'Delivered') {
        statusMatches = order.status === 'delivered';
      } else if (selectedStatus === 'Completed') {
        statusMatches = order.status === 'completed';
      } else if (selectedStatus === 'Cancelled') {
        statusMatches = order.status === 'cancelled';
      } else if (selectedStatus === 'Disputed') {
        statusMatches = order.status === 'disputed';
      }

      return statusMatches &&
        (order.id.toString().includes(searchTerm.toLowerCase()) || 
        (order.listing_title && order.listing_title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (order.buyer_username && order.buyer_username.toLowerCase().includes(searchTerm.toLowerCase())));
    })
         .sort((a, b) => {
       const aValue = a[sortField];
       const bValue = b[sortField];
       
       // Handle undefined values for sorting
       if (aValue === undefined && bValue === undefined) return 0;
       if (aValue === undefined) return sortDirection === 'asc' ? -1 : 1;
       if (bValue === undefined) return sortDirection === 'asc' ? 1 : -1;
       
       if (sortDirection === 'asc') {
         return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
       } else {
         return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
       }
     });

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleViewDetails = (order: Order) => {
    setViewingOrder(order);
    setIsModalOpen(true);
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 inline ml-1" />
      : <ArrowDownIcon className="h-4 w-4 inline ml-1" />;
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-indigo-100 text-indigo-800';
      case 'processing':
        return 'bg-orange-100 text-orange-800 font-semibold'; // Enhanced visibility for processing
      case 'pending_sale':
        return 'bg-yellow-100 text-yellow-800';
      case 'paid':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'disputed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <BackToDashboardButton customReturnPath="/seller/dashboard" buttonText="Back to Dashboard" />
        </div>
        
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">My Orders</h1>
          <p className="text-gray-600 mt-1">View and manage orders from your buyers</p>
        </div>

        {/* Order Status Summary */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{orders.length}</div>
              <div className="text-sm text-gray-600">Total Orders</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {orders.filter(o => o.status === 'pending' || o.status === 'pending_sale').length}
              </div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {orders.filter(o => o.status === 'processing').length}
              </div>
              <div className="text-sm text-gray-600">Processing</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {orders.filter(o => o.status === 'shipped').length}
              </div>
              <div className="text-sm text-gray-600">Shipped</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">
                {orders.filter(o => o.status === 'delivered').length}
              </div>
              <div className="text-sm text-gray-600">Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {orders.filter(o => o.status === 'completed').length}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {orders.filter(o => o.status === 'cancelled').length}
              </div>
              <div className="text-sm text-gray-600">Cancelled</div>
            </div>
          </div>
        </div>
        
        <div className="mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="relative w-full md:w-auto">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full md:w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <span className="text-sm font-medium text-gray-700">Filter by status:</span>
            <div className="flex flex-wrap gap-2">
              {['All', 'Pending', 'Pending Sale', 'Processing', 'Shipped', 'Delivered', 'Completed', 'Cancelled', 'Disputed'].map((status) => (
                <button
                  key={status}
                  onClick={() => setSelectedStatus(status)}
                  className={`px-3 py-1 text-sm rounded-full ${
                    selectedStatus === status
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {filteredAndSortedOrders.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-600">No orders found.</p>
            {orders.length > 0 ? (
              <p className="mt-2 text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
            ) : (
              <p className="mt-2 text-sm text-gray-500">You haven't received any orders yet.</p>
            )}
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('id')}
                    >
                      Order ID {renderSortIcon('id')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Buyer
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Item
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('price_at_purchase')}
                    >
                      Amount {renderSortIcon('price_at_purchase')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('status')}
                    >
                      Status {renderSortIcon('status')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleSort('created_at')}
                    >
                      Order Date {renderSortIcon('created_at')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">#{order.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{order.buyer_username || `User #${order.buyer_id}`}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.listing_title}</div>
                        <div className="text-xs text-gray-500">ID: {order.listing_id}</div>
                      </td>
                                             <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                         ${formatCurrency(order.total_amount || order.price_at_purchase)}
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(order.status)}`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => handleViewDetails(order)}
                          className="inline-flex items-center text-indigo-600 hover:text-indigo-900"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      <OrderDetailsModal 
        order={viewingOrder}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpdateOrder={updateOrder}
      />
    </div>
  );
};

export default SellerOrdersPage; 